import React from 'react';
import { AppIcon } from '../../../components/icons/AppIcon';
import { Button } from '../../../components/ui/Button';
import SearchInput from '../../../components/ui/SearchInput';
import { SidebarTabs, TabType } from './SidebarTabs';
import AgentsFilter from '../../../components/shared/filters/AgentsFilter';
import { ChannelInboxFilter } from './ChannelInboxFilter';
import { cn } from '@/lib/utils';
import type { CrmUser } from '../../../types';

interface SidebarToolbarProps {
    activeTab: TabType;
    onTabChange: (tab: TabType) => void;
    counters: Partial<Record<TabType, number>>;
    showArchived: boolean;
    onArchiveToggle: () => void;
    showUnreadOnly: boolean;
    onUnreadToggle: () => void;
    searchTerm: string;
    onSearchChange: (term: string) => void;
    onNewChat: () => void;
    userRole?: 'owner' | 'admin' | 'member' | 'viewer';
    users?: CrmUser[];
    currentUserId?: string | null;
    assignedUserIds?: string[];
    onAssignedUsersChange?: (ids: string[]) => void;
    channelConnectionId?: string | null;
    onChannelConnectionChange?: (connectionId: string | null) => void;
}

const iconBtnClass = (active: boolean) => cn(
    'btn-icon toolbar-icon-btn shrink-0',
    active && 'toolbar-icon-btn--active',
);

export const SidebarToolbar: React.FC<SidebarToolbarProps> = ({
    activeTab,
    onTabChange,
    counters,
    showArchived,
    onArchiveToggle,
    showUnreadOnly,
    onUnreadToggle,
    searchTerm,
    onSearchChange,
    onNewChat,
    userRole = 'member',
    users = [],
    currentUserId,
    assignedUserIds = [],
    onAssignedUsersChange,
    channelConnectionId = null,
    onChannelConnectionChange,
}) => (
    <header className="shrink-0 bg-card px-s3 pb-s2 pt-s3 md:border-b md:border-border/50">
        <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
                <h2 className="page-header-title min-w-0 truncate">Conversas</h2>
                <Button
                    type="button"
                    size="icon"
                    variant="default"
                    className="shrink-0"
                    onClick={onNewChat}
                    title="Nova conversa"
                    aria-label="Nova conversa"
                >
                    <AppIcon name="plus" className="size-4" />
                </Button>
            </div>
            <div
                className="flex shrink-0 items-center gap-1"
                role="toolbar"
                aria-label="Ações da lista de conversas"
            >
                <button
                    type="button"
                    onClick={onArchiveToggle}
                    className={iconBtnClass(showArchived)}
                    aria-pressed={showArchived}
                    title={showArchived ? 'Conversas ativas' : 'Conversas encerradas'}
                    aria-label={showArchived ? 'Conversas ativas' : 'Conversas encerradas'}
                >
                    <AppIcon name="archive" className="h-5 w-5 stroke-[1.5]" aria-hidden />
                </button>
                <button
                    type="button"
                    onClick={onUnreadToggle}
                    className={iconBtnClass(showUnreadOnly)}
                    aria-pressed={showUnreadOnly}
                    title={showUnreadOnly ? 'Mostrar todas' : 'Filtrar não lidas'}
                    aria-label={showUnreadOnly ? 'Mostrar todas' : 'Filtrar não lidas'}
                >
                    <AppIcon name="filter" className="h-5 w-5 stroke-[1.5]" aria-hidden />
                </button>
                {(userRole === 'owner' || userRole === 'admin') && onAssignedUsersChange && (
                    <AgentsFilter
                        variant="icon"
                        users={users}
                        selectedAgentIds={assignedUserIds}
                        onApply={onAssignedUsersChange}
                        currentUserId={currentUserId ?? undefined}
                    />
                )}
                {onChannelConnectionChange && (
                    <ChannelInboxFilter
                        value={channelConnectionId}
                        onChange={onChannelConnectionChange}
                    />
                )}
            </div>
        </div>

        <div className="mt-s2 w-full">
            <SearchInput
                value={searchTerm}
                onChange={onSearchChange}
                placeholder="Buscar conversa"
                inputClassName="input-sm"
            />
        </div>
        <div className="mt-s2">
            <SidebarTabs
                activeTab={activeTab}
                onTabChange={onTabChange}
                counters={counters}
                userRole={userRole}
            />
        </div>
    </header>
);

export default SidebarToolbar;
